import { type ReactNode, useId, useState } from 'react';
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/alert-dialog.js';
import { Button } from '../components/button.js';
import { Input } from '../components/input.js';

export interface ConfirmDestructiveProps {
  /**
   * The control that opens it.
   *
   * Base UI renders the trigger **as** this element rather than wrapping it, so it is one element
   * and it carries the trigger's props — the same `render` contract the dialog uses everywhere
   * else in this system.
   */
  readonly trigger?: React.ReactElement<Record<string, unknown>> | undefined;
  /**
   * Open it from code instead of from a trigger.
   *
   * For an action whose control cannot hold the dialog: a menu item unmounts when the menu closes,
   * which is the moment it is chosen, so sign-out in the account menu opens this by state (M5.5-01).
   */
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  readonly title: string;
  /** What will happen, in plain words, including what cannot be undone. */
  readonly description: ReactNode;
  /**
   * The name of the thing. When given, it must be typed before the action is allowed.
   *
   * Give it for anything genuinely irreversible — deleting a tenant, purging recordings. Leave it
   * out for something merely disruptive, where a second click is enough and typing is theatre.
   */
  readonly confirmationName?: string;
  readonly confirmationLabel?: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly onConfirm: () => void;
  readonly busy?: boolean;
}

/**
 * The last question before something irreversible.
 *
 * Built on `AlertDialog`, not `Dialog`, and that is the whole point: an alert dialog cannot be
 * dismissed by the escape key or by clicking away, because those are the gestures a person makes
 * *without deciding*, and here the absence of a decision must not look like one.
 *
 * Where a name is given it has to be typed. That is not friction for its own sake — it is the
 * difference between confirming *a* deletion and confirming *this* one, which is what somebody
 * with eight tabs open actually gets wrong.
 */
export function ConfirmDestructive({
  trigger,
  open,
  onOpenChange,
  title,
  description,
  confirmationName,
  confirmationLabel,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  busy = false,
}: ConfirmDestructiveProps) {
  const id = useId();
  const [typed, setTyped] = useState('');
  const needsName = confirmationName !== undefined;
  const confirmed = !needsName || typed.trim() === confirmationName;

  return (
    <AlertDialog
      {...(open === undefined ? {} : { open })}
      onOpenChange={(next) => {
        // Reopening starts from empty: a half-typed name left from last time is a trap.
        if (!next) {
          setTyped('');
        }
        onOpenChange?.(next);
      }}
    >
      {trigger === undefined ? null : <AlertDialogTrigger render={trigger} />}
      <AlertDialogContent>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>

        {needsName ? (
          <div className="flex flex-col gap-(--space-xs) pt-(--space-md)">
            <label
              htmlFor={id}
              className="text-(length:--font-size-small) text-(--color-text-secondary)"
            >
              {confirmationLabel ?? `Type ${confirmationName} to confirm`}
            </label>
            <Input
              id={id}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              className="font-mono"
            />
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="ghost">{cancelLabel}</Button>} />
          <Button variant="danger" disabled={!confirmed || busy} loading={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
